import {
  DiscountGroupDTO,
  FreightCondition,
  MgPackageType,
  PriceSuggestionDTO,
  ProductGroupMinimal,
  Unit,
} from '@retail/products/types';
import {
  BrandMgSupplierCampaignItemDTO,
  BrandMgSupplierCampaignItemLookupDTO,
  MemberMgSupplierCampaignItemDTO,
  MemberMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
  MgSupplierItemLookupErrorCode,
  MgSupplierItemPackageCampaignDTO,
  SupplierDTO,
} from '../dto';
import { CampaignPrice } from './CampaignPrice';

export interface MgSupplierItemLookupProps {
  mgItemNumber?: number;
  gtinCode?: string;
  supplierId?: number;
}

export class MgSupplierItemLookup {
  mgItemNumber?: number;
  gtinCode?: string;
  supplierId?: number;

  constructor({ mgItemNumber, gtinCode, supplierId }: MgSupplierItemLookupProps) {
    this.mgItemNumber = mgItemNumber;
    this.gtinCode = gtinCode;
    this.supplierId = supplierId;
  }

  static fromDto(dto: MgSupplierItemLookupDTO): MgSupplierItemLookup {
    return new MgSupplierItemLookup({
      mgItemNumber: dto.mgItemNumber,
      gtinCode: dto.gtinCode,
      supplierId: dto.supplierId,
    });
  }
}

export interface SupplierProps {
  id: number;
  name: string;
  isMainSupplier?: boolean;
}

export class Supplier {
  id: number;
  name: string;
  isMainSupplier?: boolean;

  constructor({ id, name, isMainSupplier }: SupplierProps) {
    this.id = id;
    this.name = name;
    this.isMainSupplier = isMainSupplier;
  }

  static fromDto(dto: SupplierDTO): Supplier {
    return new Supplier({
      id: dto.id,
      name: dto.name,
      isMainSupplier: dto.mainSupplier,
    });
  }
}

export interface MgSupplierItemPackageCampaignProps {
  calculatedCount: number;
  gtinCode?: string;
  packageType: MgPackageType;
  unit: Unit;
  isBasePriceUnit?: boolean;
  classCode: string;
  isPriceComparisonUnit?: boolean;
}

export class MgSupplierItemPackageCampaign {
  calculatedCount: number;
  gtinCode?: string;
  packageType: MgPackageType;
  unit: Unit;
  isBasePriceUnit?: boolean;
  classCode: string;
  isPriceComparisonUnit?: boolean;

  constructor({
    calculatedCount,
    gtinCode,
    packageType,
    unit,
    isBasePriceUnit,
    classCode,
    isPriceComparisonUnit,
  }: MgSupplierItemPackageCampaignProps) {
    this.calculatedCount = calculatedCount;
    this.gtinCode = gtinCode;
    this.packageType = packageType;
    this.unit = unit;
    this.isBasePriceUnit = isBasePriceUnit;
    this.classCode = classCode;
    this.isPriceComparisonUnit = isPriceComparisonUnit;
  }

  static fromDto(dto: MgSupplierItemPackageCampaignDTO): MgSupplierItemPackageCampaign {
    return new MgSupplierItemPackageCampaign({
      calculatedCount: dto.calculatedCount,
      gtinCode: dto.gtinCode,
      packageType: dto.packageType,
      unit: Unit.fromDto(dto.unit),
      isBasePriceUnit: dto.basePriceUnit,
      classCode: dto.classCode,
      isPriceComparisonUnit: dto.priceComparisonUnit,
    });
  }
}

export interface BaseMgSupplierCampaignItemProps {
  productGroup: ProductGroupMinimal;
  finfoNumber: number | undefined;
  nobbNumber: number | undefined;
  mgItemNumber: number;
  mgSupplierItemId: number;
  primaryText: string;
  secondaryText?: string;
  isInAssortment?: boolean;
  discountGroup?: DiscountGroupDTO;
  supplier: Supplier;
  priceSuggestions: PriceSuggestionDTO[];
  packages: MgSupplierItemPackageCampaign[];
}

export class BaseMgSupplierCampaignItem {
  productGroup: ProductGroupMinimal;
  finfoNumber?: number;
  nobbNumber?: number;
  mgItemNumber: number;
  mgSupplierItemId: number;
  primaryText: string;
  secondaryText?: string;
  isInAssortment?: boolean;
  discountGroup?: DiscountGroupDTO;
  supplier: Supplier;
  priceSuggestions: PriceSuggestionDTO[];
  packages: MgSupplierItemPackageCampaign[];

  protected constructor({
    productGroup,
    finfoNumber,
    nobbNumber,
    mgItemNumber,
    mgSupplierItemId,
    primaryText,
    secondaryText,
    isInAssortment,
    discountGroup,
    supplier,
    priceSuggestions,
    packages,
  }: BaseMgSupplierCampaignItemProps) {
    this.productGroup = productGroup;
    this.finfoNumber = finfoNumber;
    this.nobbNumber = nobbNumber;
    this.mgItemNumber = mgItemNumber;
    this.mgSupplierItemId = mgSupplierItemId;
    this.primaryText = primaryText;
    this.secondaryText = secondaryText;
    this.isInAssortment = isInAssortment;
    this.discountGroup = discountGroup;
    this.supplier = supplier;
    this.priceSuggestions = priceSuggestions;
    this.packages = packages;
  }

  getPrimaryPackageUnit = () => this.packages.find((p) => p.packageType === 'PRIMARY')?.unit;
}

export class BrandMgSupplierCampaignItem extends BaseMgSupplierCampaignItem {
  static fromDto(dto: BrandMgSupplierCampaignItemDTO): BrandMgSupplierCampaignItem {
    return new BrandMgSupplierCampaignItem({
      productGroup: dto.productGroup,
      finfoNumber: dto.finfoNumber ?? dto.finfoValues?.finfoNumber,
      nobbNumber: dto.nobbNumber,
      mgItemNumber: dto.mgItemNumber,
      mgSupplierItemId: dto.mgSupplierItemId,
      primaryText: dto.primaryText,
      secondaryText: dto.secondaryText,
      isInAssortment: dto.inAssortment,
      discountGroup: dto.discountGroup,
      supplier: Supplier.fromDto(dto.supplier),
      priceSuggestions: dto.priceSuggestions,
      packages: dto.packages.map((pkg) => MgSupplierItemPackageCampaign.fromDto(pkg)),
    });
  }
}
export interface Prices {
  primaryIncVat?: number;
  primaryExVat?: number;
  secondaryIncVat?: number;
  secondaryExVat?: number;
  tertiaryIncVat?: number;
  tertiaryExVat?: number;
  purchasePriceExVat?: number;
  coverageDegree?: number;
}
interface MemberMgSupplierCampaignItemProps extends BaseMgSupplierCampaignItemProps {
  localPurchaseUnitPrices?: CampaignPrice[];
  localFreightCondition?: FreightCondition;
  localCostUnitPrices?: CampaignPrice[];
  localSalesUnitPrices?: CampaignPrice[];
  prices?: Prices;
  locale: string;
}

export class MemberMgSupplierCampaignItem extends BaseMgSupplierCampaignItem {
  localPurchaseUnitPrices?: CampaignPrice[];
  localFreightCondition?: FreightCondition;
  localCostUnitPrices?: CampaignPrice[];
  localSalesUnitPrices?: CampaignPrice[];
  prices?: Prices;
  locale: string;

  constructor({
    localPurchaseUnitPrices,
    localFreightCondition,
    localCostUnitPrices,
    localSalesUnitPrices,
    prices,
    locale,
    ...props
  }: MemberMgSupplierCampaignItemProps) {
    super(props);
    this.localPurchaseUnitPrices = localPurchaseUnitPrices;
    this.localFreightCondition = localFreightCondition;
    this.localCostUnitPrices = localCostUnitPrices;
    this.localSalesUnitPrices = localSalesUnitPrices;
    this.prices = prices;
    this.locale = locale;
  }

  findLocalPurchasePriceForUnit = (unit: string) =>
    this.localPurchaseUnitPrices?.find((price) => price.unit.unit === unit);

  findLocalCostPriceForUnit = (unit: string) =>
    this.localCostUnitPrices?.find((price) => price.unit.unit === unit);

  findLocalSalesPriceForUnit = (unit: string) =>
    this.localSalesUnitPrices?.find((price) => price.unit.unit === unit);

  getFormattedSalesCoverage = (): string | undefined => {
    const salesCoverage = this.prices?.coverageDegree;
    return salesCoverage != null ? `${Number(salesCoverage.toFixed(2))}%` : undefined;
  };

  static fromDto = (
    dto: MemberMgSupplierCampaignItemDTO,
    locale: string
  ): MemberMgSupplierCampaignItem =>
    new MemberMgSupplierCampaignItem({
      productGroup: dto.productGroup,
      finfoNumber: dto.finfoNumber,
      nobbNumber: dto.nobbNumber,
      mgItemNumber: dto.mgItemNumber,
      mgSupplierItemId: dto.mgSupplierItemId,
      primaryText: dto.primaryText,
      secondaryText: dto.secondaryText,
      isInAssortment: dto.inAssortment,
      discountGroup: dto.discountGroup,
      supplier: Supplier.fromDto(dto.supplier),
      priceSuggestions: dto.priceSuggestions,
      packages: dto.packages.map((pkg) => MgSupplierItemPackageCampaign.fromDto(pkg)),
      prices: dto.prices,
      locale,
    });
}

export interface MgSupplierCampaignItemLookupProps<T extends BaseMgSupplierCampaignItem> {
  input: MgSupplierItemLookup;
  errorCode?: MgSupplierItemLookupErrorCode;
  items?: T[];
}

export class BaseMgSupplierCampaignItemLookup<T extends BaseMgSupplierCampaignItem> {
  input: MgSupplierItemLookup;
  errorCode?: MgSupplierItemLookupErrorCode;
  items?: T[];

  protected constructor({ input, errorCode, items }: MgSupplierCampaignItemLookupProps<T>) {
    this.input = input;
    this.errorCode = errorCode;
    this.items = items;
  }
}

export class BrandMgSupplierCampaignItemLookup extends BaseMgSupplierCampaignItemLookup<BrandMgSupplierCampaignItem> {
  static fromDto(dto: BrandMgSupplierCampaignItemLookupDTO): BrandMgSupplierCampaignItemLookup {
    return new BrandMgSupplierCampaignItemLookup({
      input: MgSupplierItemLookup.fromDto(dto.input),
      errorCode: dto.errorCode,
      items: dto.items?.map(BrandMgSupplierCampaignItem.fromDto),
    });
  }
}

export class MemberMgSupplierCampaignItemLookup extends BaseMgSupplierCampaignItemLookup<MemberMgSupplierCampaignItem> {
  static fromDto(
    dto: MemberMgSupplierCampaignItemLookupDTO,
    locale: string
  ): MemberMgSupplierCampaignItemLookup {
    return new MemberMgSupplierCampaignItemLookup({
      input: MgSupplierItemLookup.fromDto(dto.input),
      errorCode: dto.errorCode,
      items: dto.items?.map((item) => MemberMgSupplierCampaignItem.fromDto(item, locale)),
    });
  }
}
